import React, { useEffect, useState } from 'react';
import { Modal, Table, Checkbox, Tag, Button, Space } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

/* ==================== TYPES ==================== */

interface AllotmentRow {
  bpptm_id: number;          // ✅ UNIQUE product type id
  bpptm_name: string;
  pbpm_id: string | null;    // mapping id (null = not mapped)
  mapped: 'Y' | 'N';
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  bodyPartId: number | null;
  existingAllotments: {
    bpptm_id: number;
    bpptm_name: string;
    pbpm_id: string | null;
  }[];
  onSave: () => void;
}

/* ==================== COMPONENT ==================== */

const BodyPartProducttypeAllotmentModal: React.FC<Props> = ({
  visible,
  onCancel,
  bodyPartId,
  existingAllotments,
  onSave,
}) => {
  const [rows, setRows] = useState<AllotmentRow[]>([]);
  const [loading, setLoading] = useState(false);

  /* ==================== LOAD DATA ==================== */

  useEffect(() => {
    if (!visible) return;

    const mappedRows: AllotmentRow[] = existingAllotments.map(item => ({
      bpptm_id: item.bpptm_id,
      bpptm_name: item.bpptm_name,
      pbpm_id: item.pbpm_id ?? null,
      mapped: item.pbpm_id ? 'Y' : 'N',
    }));

    setRows(mappedRows);
  }, [existingAllotments, visible]);

  /* ==================== TOGGLE ==================== */

  const toggleAllotment = (bpptm_id: number, checked: boolean) => {
    setRows(prev =>
      prev.map(row =>
        row.bpptm_id === bpptm_id
          ? { ...row, mapped: checked ? 'Y' : 'N' }
          : row
      )
    );
  };

  /* ==================== SAVE ==================== */

  const handleSave = async () => {
    if (!bodyPartId) return;
    setLoading(true);

    try {
      // ✅ collect selected items
      const selectedItems = rows
        .filter(row => row.mapped === 'Y')
        .map(row => ({
          bpptm_id: row.bpptm_id,
        }));

      if (!selectedItems.length) {
        showError('Please select at least one product type');
        setLoading(false);
        return;
      }

      // ✅ single API call
      await apiRequest({
        method: 'post',
        url: 'add-body-part-product-type-allotment',
        data: {
          bpptm_body_part_id: bodyPartId,
          items: selectedItems,
        },
      });

      showSuccess('Product type allotments saved successfully');
      onSave();
      onCancel();
    } catch (error) {
      console.error(error);
      showError('Failed to save product type allotments');
    } finally {
      setLoading(false);
    }
  };


  /* ==================== TABLE COLUMNS ==================== */

  const columns = [
    {
      title: 'Product Type',
      dataIndex: 'bpptm_name',
      key: 'bpptm_name',
    },
    {
      title: 'Mapped Status',
      dataIndex: 'mapped',
      key: 'mapped',
      render: (value: 'Y' | 'N') =>
        value === 'Y'
          ? <Tag color="green">Mapped</Tag>
          : <Tag>Not Mapped</Tag>,
    },
    {
      title: 'Select',
      key: 'select',
      render: (_: any, record: AllotmentRow) => (
        <Checkbox
          checked={record.mapped === 'Y'}
          onChange={e =>
            toggleAllotment(record.bpptm_id, e.target.checked)
          }
        />
      ),
    },
  ];

  /* ==================== RENDER ==================== */

  return (
    <Modal
      title="Body Part – Product Type Mapping"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Table
        rowKey="bpptm_id"
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ y: 500 }}
      />

      <Space
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={loading} onClick={handleSave}>
          Save
        </Button>
      </Space>
    </Modal>
  );
};

export default BodyPartProducttypeAllotmentModal;
